import { useCallback, useEffect, useMemo } from 'react';

import { ErrorCode, FileRejection } from 'react-dropzone';

import { useCurrentProjectId, useUser } from '@adsk/alloy-provider';
import {
  useNotifications,
  NOTIFICATION_STATUSES,
} from '@adsk/alloy-react-notification';
import { UploadUtil } from '@adsk/docs-js-sdk';

import useClientSideAttachmentsModifications from './useClientSideAttachmentsModifications';
import useContextualTrack from './useContextualTrack';
import useDrafts from './useDrafts';
import {
  createAttachmentUrl,
  getAttachmentType,
  uploadFileToOSS,
} from '../api/attachments';
import { DownloadMethod, UploadMethod } from '../consts';
import { TAttachmentsContext } from '../context/AttachmentsContext';
import { AttachmentsProviderProps } from '../context/AttachmentsProvider';
import { useTmpLocalViews } from '../context/LocalViewsProvider';
import createDraft from '../helpers/createDraft';
import createRejectedDraft from '../helpers/createRejectedDraft';
import formatFileSize from '../helpers/formatFileSize';
import { useTranslation } from '../i18n';
import { AttachmentDraft, Attachment } from '../types';

type UseAttachmentsProps = Pick<
  AttachmentsProviderProps,
  | 'handleLinkAttachment'
  | 'onDeleteAttachment'
  | 'attachments'
  | 'maxFileSize'
  | 'onBeforeDownload'
  | 'onAfterDownload'
  | 'onDownloadFailed'
  | 'onBeforeUpload'
  | 'onAfterUpload'
  | 'onUploadFailed'
  | 'onBeforeDelete'
  | 'onAfterDelete'
  | 'onDeleteFailed'
  | 'onDeleteDraft'
> & {
  onTooManyAttachmentsError?: () => void;
};

type UseAttachmentsReturnValue = {
  addAttachments: ({
    files,
    method,
  }: {
    files: File[];
    method: UploadMethod;
  }) => void;
  deleteAttachment: (attachmentId: string) => void;
  deleteDraft: (attachmentId: string) => void;
  discardUnlinkedDrafts: () => void;
  attachments: Attachment[];
  drafts: AttachmentDraft[];
  uploadIndicationIds: string[];
  downloadAttachment: ({
    attachmentId,
    method,
  }: {
    attachmentId: string;
    method: DownloadMethod;
  }) => void;
  openPDFAttachmentInNewTab: ({
    attachmentId,
  }: {
    attachmentId: string;
  }) => void;
  retryDraftUpload: (draftId: string) => void;
  onRetryLoadingAttachments?: () => void;
  contextualTrack: (eventName: string, data: Record<string, unknown>) => void;
  isUploading: boolean;
  handleRejectedAttachments: TAttachmentsContext['handleRejectedAttachments'];
  hideAttachmentUploadIndication: (attachmentId: string) => void;
};

const useAttachmentsManager = ({
  maxFileSize,
  handleLinkAttachment,
  onDeleteAttachment,
  attachments: attachmentsFromProp,
  // Upload hooks
  onBeforeUpload: onBeforeUploadFromProp,
  onAfterUpload: onAfterUploadFromProp,
  onUploadFailed: onUploadFailedFromProp,
  // Delete hooks
  onBeforeDelete: onBeforeDeleteFromProp,
  onAfterDelete: onAfterDeleteFromProp,
  onDeleteFailed: onDeleteFailedFromProp,
  onDeleteDraft,
  // Download hooks
  onBeforeDownload: onBeforeDownloadFromProp,
  onAfterDownload: onAfterDownloadFromProp,
  onDownloadFailed: onDownloadFailedFromProp,
  // Error hooks
  onTooManyAttachmentsError,
}: UseAttachmentsProps): UseAttachmentsReturnValue => {
  const { user } = useUser();
  const projectId = useCurrentProjectId();
  const { contextualTrack } = useContextualTrack();
  const { showNotification } = useNotifications();
  const { t } = useTranslation();

  if (!projectId) {
    throw new Error('Project ID is required to upload attachments');
  }

  const {
    deletedAttachments,
    deleteAttachment: deleteLocalAttachment,
    uploadIndicationIds,
    showUploadIndicationAttachment,
    hideAttachmentUploadIndication,
  } = useClientSideAttachmentsModifications();

  const { addLocalView, removeLocalViewIfExist } = useTmpLocalViews();

  const {
    drafts,
    addDrafts,
    updateDraft,
    deleteDraft,
    onDraftUploadFailed,
    discardUnlinkedDrafts,
    deleteDraftAndFileFromOSS,
  } = useDrafts(onDeleteDraft);

  const attachments = useMemo(() => {
    return attachmentsFromProp
      ? attachmentsFromProp
          .filter(
            (attachment) =>
              !deletedAttachments.includes(attachment.attachmentId),
          )
          .toSorted((a, b) => {
            return (
              new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime()
            );
          })
      : [];
  }, [deletedAttachments, attachmentsFromProp]);

  useEffect(() => {
    const attachmentsIds = attachments.map(
      (attachment) => attachment.attachmentId,
    );

    drafts.forEach((draft) => {
      if (attachmentsIds.includes(draft.attachmentId)) {
        deleteDraft(draft.attachmentId);
      }
    });
  }, [attachments, drafts, deleteDraft]);

  /**
   * Check if there are any drafts that are being uploaded
   */
  const isUploading = useMemo(
    () =>
      drafts.some(
        (draft) => !draft.uploaded && !draft.error && !draft.rejected,
      ),
    [drafts],
  );

  /**
   * Upload a file to OSS, and conditionally executes handleLinkAttachment if provided
   */
  const uploadAttachmentToOSS = useCallback(
    async ({
      draft,
      onBeforeUpload,
      onAfterUpload,
      onUploadFailed,
    }: {
      draft: AttachmentDraft;
      onBeforeUpload?: () => void;
      onAfterUpload?: () => void;
      onUploadFailed?: (error?: unknown) => void;
    }) => {
      if (onBeforeUpload) {
        onBeforeUpload();
      }

      try {
        addLocalView(draft);
        const possibleError = await UploadUtil.validateFile(draft.file);

        if ('code' in possibleError) {
          throw possibleError;
        }
      } catch (error) {
        onDraftUploadFailed({
          draftId: draft.attachmentId,
          error,
        });

        if (onUploadFailed) {
          onUploadFailed(error);
        }

        return;
      }

      try {
        const rawUnlinkedAttachment = await uploadFileToOSS({
          draft,
          projectId,
        });

        const unlinkedAttachment = {
          ...rawUnlinkedAttachment,
          createdBy: user?.autodeskId || undefined,
        };

        if (handleLinkAttachment) {
          await handleLinkAttachment(unlinkedAttachment);
        }

        updateDraft({
          draftId: draft.attachmentId,
          data: unlinkedAttachment,
        });

        showUploadIndicationAttachment(draft.attachmentId);

        if (onAfterUpload) {
          onAfterUpload();
        }
      } catch (error) {
        onDraftUploadFailed({
          draftId: draft.attachmentId,
          error,
        });

        if (onUploadFailed) {
          onUploadFailed(error);
        }
      }
    },
    [
      onDraftUploadFailed,
      projectId,
      user?.autodeskId,
      handleLinkAttachment,
      addLocalView,
      updateDraft,
      showUploadIndicationAttachment,
    ],
  );

  /**
   * Calculates dropzone error message based on the error code
   */
  const getDropzoneRejectionErrorMessage = useCallback(
    (errors: FileRejection['errors']) => {
      const error = errors[0];
      if (error.code === ErrorCode.FileTooLarge && maxFileSize) {
        return t('SIZE_LIMIT_EXCEEDED', {
          maxSize: formatFileSize(maxFileSize),
        }) as unknown as string;
      } else if (error.code === ErrorCode.FileInvalidType) {
        return t('INVALID_FILE_TYPE') as unknown as string;
      }

      return t('GENERAL_ERROR') as unknown as string;
    },
    [t, maxFileSize],
  );

  /**
   * Handles rejected attachments
   */
  const handleRejectedAttachments: TAttachmentsContext['handleRejectedAttachments'] =
    useCallback(
      ({ files }) => {
        const hasTooManyFilesError = files.some((file) =>
          file.errors.some((error) => error.code === ErrorCode.TooManyFiles),
        );

        if (hasTooManyFilesError) {
          onTooManyAttachmentsError?.();
          return;
        }

        const newDrafts = files.map((file) =>
          createRejectedDraft({
            file,
            error: getDropzoneRejectionErrorMessage(file.errors),
          }),
        );

        addDrafts(newDrafts);
      },
      [addDrafts, getDropzoneRejectionErrorMessage, onTooManyAttachmentsError],
    );

  /**
   * Handles attachment upload & analytics
   */
  const addAttachments = useCallback(
    async ({ files, method }: { files: File[]; method: UploadMethod }) => {
      const newDrafts = files.map((file) => {
        if (file.size === 0) {
          return createRejectedDraft({
            file: { file } as FileRejection,
            error: t('EMPTY_FILE_NOT_SUPPORTED'),
          });
        }
        return createDraft({ file });
      });
      addDrafts(newDrafts);

      await Promise.all(
        newDrafts.map((draft) => {
          if (draft.rejected) {
            return Promise.resolve();
          }
          return uploadAttachmentToOSS({
            draft,
            onBeforeUpload: () => {
              contextualTrack('prodex.attachments.add.start', {
                file_type: getAttachmentType(draft.file.name),
                method,
              });

              if (onBeforeUploadFromProp) {
                onBeforeUploadFromProp();
              }
            },
            onAfterUpload: () => {
              contextualTrack('prodex.attachments.add.complete', {
                file_type: getAttachmentType(draft.file.name),
                method,
              });

              if (onAfterUploadFromProp) {
                onAfterUploadFromProp();
              }
            },
            onUploadFailed: (error) => {
              contextualTrack('prodex.attachments.add.complete', {
                file_type: getAttachmentType(draft.file.name),
                method,
                error: true,
              });

              if (onUploadFailedFromProp) {
                onUploadFailedFromProp(error);
              }

              console.error('[Attachments] failed to upload attachment', error);
            },
          });
        }),
      );
    },
    [
      addDrafts,
      t,
      uploadAttachmentToOSS,
      contextualTrack,
      onBeforeUploadFromProp,
      onAfterUploadFromProp,
      onUploadFailedFromProp,
    ],
  );

  /**
   * Retry uploading a draft
   */
  const retryDraftUpload = useCallback(
    async (draftId: string) => {
      const draft = drafts.find((draft) => draft.attachmentId === draftId);

      if (!draft) {
        return;
      }

      updateDraft({
        draftId,
        data: {
          error: undefined,
        },
      });

      await uploadAttachmentToOSS({ draft });
    },
    [drafts, updateDraft, uploadAttachmentToOSS],
  );

  /**
   * Handles attachment delete request
   */
  const deleteAttachment = useCallback(
    async (attachmentId: string) => {
      const attachmentToDelete = attachments.find(
        (attachment) => attachment.attachmentId === attachmentId,
      ) as Attachment;

      if (onBeforeDeleteFromProp) {
        onBeforeDeleteFromProp();
      }

      contextualTrack('prodex.attachments.delete.start', {
        file_type: attachmentToDelete.fileType,
      });

      try {
        removeLocalViewIfExist(attachmentId);

        if (onDeleteAttachment) {
          await onDeleteAttachment(attachmentToDelete);
        }

        deleteLocalAttachment(attachmentId);

        contextualTrack('prodex.attachments.delete.complete', {
          file_type: attachmentToDelete.fileType,
          result: 'success',
        });

        if (onAfterDeleteFromProp) {
          onAfterDeleteFromProp();
        }
      } catch (error) {
        contextualTrack('prodex.attachments.delete.complete', {
          file_type: attachmentToDelete.fileType,
          result: 'error',
        });

        if (onDeleteFailedFromProp) {
          onDeleteFailedFromProp(error);
        }

        console.error('[Attachments] failed to delete attachment', error);

        throw error;
      }

      deleteLocalAttachment(attachmentId);
    },
    [
      attachments,
      onBeforeDeleteFromProp,
      contextualTrack,
      deleteLocalAttachment,
      removeLocalViewIfExist,
      onDeleteAttachment,
      onAfterDeleteFromProp,
      onDeleteFailedFromProp,
    ],
  );

  /**
   * Download an attachment
   */
  const downloadAttachment = useCallback(
    async ({
      attachmentId,
      method,
    }: {
      attachmentId: string;
      method: DownloadMethod;
    }) => {
      const attachmentToDownload = attachments.find(
        (attachment) => attachment.attachmentId === attachmentId,
      ) as Attachment;
      onBeforeDownloadFromProp?.(attachmentToDownload);

      contextualTrack('prodex.attachments.download.start', {
        file_type: attachmentToDownload.fileType,
        method,
      });

      try {
        const url = await createAttachmentUrl({
          projectId,
          fileUrn: attachmentToDownload.tipVersionUrn,
          // this is used to set the filename when downloading the attachment
          filename: attachmentToDownload.displayName,
          // this is used to set the content disposition header, to allow opening pdf in a new tab instead of downloading it
          contentDisposition: 'attachment',
          contentType: 'application/octet-stream',
          errorMessage: 'Failed to get download link',
        });

        window.location.assign(url);

        contextualTrack('prodex.attachments.download.complete', {
          file_type: attachmentToDownload.fileType,
          method,
          result: 'success',
        });

        onAfterDownloadFromProp?.(attachmentToDownload);
      } catch (e) {
        showNotification({
          status: NOTIFICATION_STATUSES.ERROR,
          message: t('FAILED_TO_DOWNLOAD_ATTACHMENT'),
        });

        contextualTrack('prodex.attachments.download.complete', {
          file_type: attachmentToDownload.fileType,
          method,
          result: 'error',
        });

        console.error('[Attachments] failed to download attachment', e);

        onDownloadFailedFromProp?.(e);
      }
    },
    [
      attachments,
      projectId,
      showNotification,
      t,
      contextualTrack,
      onBeforeDownloadFromProp,
      onAfterDownloadFromProp,
      onDownloadFailedFromProp,
    ],
  );

  /**
   * We have decided to open PDF in new tab instead of downloading it on item click, therefore we use before after and fail handlers like download attachment
   */
  const openPDFAttachmentInNewTab = useCallback(
    async ({ attachmentId }: { attachmentId: string }) => {
      const attachmentToWatch = attachments.find(
        (attachment) => attachment.attachmentId === attachmentId,
      ) as Attachment;

      onBeforeDownloadFromProp?.(attachmentToWatch);

      contextualTrack('prodex.attachments.open_new_tab.start', {
        file_type: attachmentToWatch.fileType,
        method: 'click',
      });

      try {
        const url = await createAttachmentUrl({
          projectId,
          fileUrn: attachmentToWatch.tipVersionUrn,
          // this is used to set the filename when downloading the attachment
          filename: attachmentToWatch.displayName,
          // this is used to set the content disposition header, to allow opening pdf in a new tab instead of downloading it
          contentDisposition: 'inline',
          contentType: 'application/pdf',
          errorMessage: 'Failed to get watchable link',
        });

        window.open(url, '_blank');

        contextualTrack('prodex.attachments.open_new_tab.complete', {
          file_type: attachmentToWatch.fileType,
          method: 'click',
          result: 'success',
        });

        onAfterDownloadFromProp?.(attachmentToWatch);
      } catch (e) {
        showNotification({
          status: NOTIFICATION_STATUSES.ERROR,
          message: t('FAILED_TO_OPEN_TAB_ATTACHMENT'),
        });

        contextualTrack('prodex.attachments.open_new_tab.complete', {
          file_type: attachmentToWatch.fileType,
          method: 'click',
          result: 'error',
        });

        console.error('[Attachments] failed to open attachment in new tab', e);

        onDownloadFailedFromProp?.(e);
      }
    },
    [
      attachments,
      onBeforeDownloadFromProp,
      contextualTrack,
      projectId,
      onAfterDownloadFromProp,
      showNotification,
      t,
      onDownloadFailedFromProp,
    ],
  );

  return {
    attachments,
    drafts,
    uploadIndicationIds,
    hideAttachmentUploadIndication,
    addAttachments,
    deleteAttachment,
    deleteDraft: deleteDraftAndFileFromOSS,
    discardUnlinkedDrafts,
    downloadAttachment,
    openPDFAttachmentInNewTab,
    retryDraftUpload,
    contextualTrack,
    isUploading,
    handleRejectedAttachments,
  };
};

export default useAttachmentsManager;
